import { IntlProvider } from "react-intl";
import en from "../../locale/en.json";
import React, { useCallback, useEffect, useState } from "react";
import { noticeError } from "../../services/monitoring";
import { getLanguagePrefix } from "../utils/locale";
import "@formatjs/intl-listformat/polyfill";
import "@formatjs/intl-listformat/locale-data/en";
import "@formatjs/intl-pluralrules/polyfill";
import "@formatjs/intl-pluralrules/locale-data/en";
import "@formatjs/intl-locale/polyfill";

function importLocalePolyfills(lang) {
  return Promise.all([
    import(`@formatjs/intl-listformat/locale-data/${lang}`),
    import(`@formatjs/intl-pluralrules/locale-data/${lang}`),
  ]);
}

function loadPolyfill(fullLocale) {
  return importLocalePolyfills(fullLocale)
    .catch(() => {
      const prefix = getLanguagePrefix(fullLocale);
      // e.g. de-DE is just de
      if (prefix !== fullLocale) {
        return importLocalePolyfills(prefix);
      }
    })
    .catch(() => {
      // don't blow up if we have some invalid locale
    });
}

export default function LocaleProvider({ children, locale }) {
  const [messages, setMessages] = useState<Record<string, string>>();

  const updateMessages = useCallback((messages) => {
    const fixedMessages = { ...messages };
    delete fixedMessages.smartling;
    setMessages(fixedMessages);
  }, []);

  const initEnglish = useCallback(() => {
    updateMessages(en);
  }, [updateMessages]);

  useEffect(() => {
    if (locale === "en-US") {
      initEnglish();
    } else {
      loadPolyfill(locale).then(() => {
        import(`../../locale/${locale}.json`)
          .then((response) => {
            updateMessages({
              ...en,
              ...response.default,
            });
          })
          .catch(() => {
            initEnglish();
          });
      });
    }
  }, [locale, updateMessages, initEnglish]);

  if (!messages) {
    return null;
  }

  return (
    <IntlProvider locale={locale} messages={messages} onError={noticeError}>
      {children}
    </IntlProvider>
  );
}
